import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './app/App';
import './index.css';
import * as Sentry from '@sentry/browser';
import { PostHogProvider } from 'posthog-js/react';
import { Analytics } from '@vercel/analytics/react';
import ErrorBoundary from './shared/components/ErrorBoundary';

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((reg) => reg.unregister());
  });
}

// Initialize Sentry only if DSN is configured
if (import.meta.env.VITE_PUBLIC_SENTRY_DSN) {
  try {
    Sentry.init({
      dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
      environment: import.meta.env.VITE_PUBLIC_APP_ENV || 'development',
      // Performance Monitoring
      tracesSampleRate: import.meta.env.VITE_PUBLIC_APP_ENV === 'production' ? 0.1 : 1.0,
      initialScope: {
        tags: {
          type: 'frontend',
          projectId: import.meta.env.VITE_PUBLIC_APP_ID,
        },
      },
      beforeSend(event, hint) {
        // Don't send "Admin access required" errors to Sentry
        const errorMessage = 
          event.message || 
          hint?.originalException?.message || 
          hint?.originalException?.toString() ||
          event.exception?.values?.[0]?.value ||
          '';
        
        if (errorMessage.includes('Admin access required')) {
          return null; // Don't send this event to Sentry
        }
        
        // Log to console in development
        if (import.meta.env.VITE_PUBLIC_APP_ENV === 'development') {
          console.error('Sentry Event:', event);
          console.error('Sentry Hint:', hint);
        }
        
        // Add user context if available
        try {
          const userEmail = document.querySelector('[data-user-email]')?.getAttribute('data-user-email');
          if (userEmail) {
            event.user = {
              email: userEmail,
            };
          }
        } catch (e) {
          // Ignore errors getting user context
        }
        
        // Add browser context
        event.contexts = {
          ...event.contexts,
          browser: {
            name: navigator.userAgent,
            version: navigator.appVersion,
          },
        };
        
        return event;
      },
      // Capture unhandled errors and promise rejections
      captureUnhandledRejections: true,
    });

    // Set up global error handlers for additional context
    window.addEventListener('error', (event) => {
      Sentry.captureException(event.error || new Error(event.message), {
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          message: event.message,
          source: event.filename || 'unknown',
          timestamp: new Date().toISOString(),
        },
        tags: {
          errorType: 'unhandledError',
          hasError: !!event.error,
        },
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      // Prevent default browser console error for unhandled rejections
      // We're handling it ourselves
      event.preventDefault();
      
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));
      
      Sentry.captureException(error, {
        extra: {
          type: 'unhandledPromiseRejection',
          reason: String(event.reason),
          timestamp: new Date().toISOString(),
        },
        tags: {
          errorType: 'unhandledRejection',
        },
      });
    });

    console.log('Sentry initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
} else {
  console.warn('Sentry DSN not configured. Error tracking is disabled.');
}

// PostHog configuration
const postHogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  // Only load PostHog in production
  loaded: (posthog) => {
    if (import.meta.env.VITE_PUBLIC_APP_ENV !== 'production') {
      posthog.opt_out_capturing();
    }
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <PostHogProvider
        apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
        options={postHogOptions}
      >
        <App />
        <Analytics />
      </PostHogProvider>
    </ErrorBoundary>
  </React.StrictMode>
);